// Ayuda contextual del módulo Trading (bot Binance). Trading → Resumen → Ayuda.
// Patrón ayuda-de-modulo: checklist en vivo + flujo + ¿cómo hago...? + glosario.
// OJO: la cuenta de trading es GLOBAL (sin tenant) y los setters exigen nivel 5.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, LifeBuoy, Link2, Loader2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useTabsStore } from '@/stores/tabs.store';
import { apiClient } from '@/lib/api/client';
import { LOCALE } from '@/lib/utils/regional';

const TRD = { headers: { 'Content-Profile': 'trading', 'Accept-Profile': 'trading' } };

interface AyudaData {
  checklist: Record<string, boolean>;
  estado: {
    ambiente: string | null; symbol: string | null; estrategia: string | null;
    kill_switch: boolean; kill_switch_motivo: string | null;
    balance_usd: number | null; pnl_dia_usd: number | null; posiciones: number | null;
    heartbeat: string | null; mensaje: string | null;
  } | null;
}

function useIr() {
  const openTab = useTabsStore((s) => s.openTab);
  return (path: string, title: string) => openTab({ id: path, title, path });
}

const CHECKS: { key: string; label: string; ruta: string; titulo: string; ayuda: string; advisory?: boolean }[] = [
  { key: 'config', label: 'Configuración creada', ruta: '/trading/binance', titulo: 'Binance', ayuda: 'Estrategia, par (symbol), timeframe y parámetros de riesgo.' },
  { key: 'api_keys', label: 'Llaves API de Binance configuradas', ruta: '/trading/binance', titulo: 'Binance', ayuda: 'Se guardan CIFRADAS; solo el motor del bot puede descifrarlas. Usa llaves SIN permiso de retiro.' },
  { key: 'estrategia', label: 'Estrategia y par definidos', ruta: '/trading/binance', titulo: 'Binance', ayuda: 'Qué opera el bot (ej. BTC/USDT) y con qué lógica.' },
  { key: 'bot_activo', label: 'Bot habilitado (activo)', ruta: '/trading/binance', titulo: 'Binance', ayuda: 'El interruptor maestro: apagado, el motor no abre posiciones.' },
  { key: 'sin_kill_switch', label: 'Kill switch APAGADO', ruta: '/trading/binance', titulo: 'Binance', ayuda: 'Si está encendido, el bot se detiene de emergencia (no abre ni mantiene posiciones).' },
  { key: 'bot_conectado', label: 'Motor Python conectado', ruta: '/trading/dashboard', titulo: 'Dashboard Trading', ayuda: 'El proceso trading-bot corre en el servidor y está conectado a la BD.', advisory: true },
  { key: 'heartbeat_ok', label: 'Heartbeat reciente (< 5 min)', ruta: '/trading/dashboard', titulo: 'Dashboard Trading', ayuda: 'El motor reporta señales de vida en cada ciclo.', advisory: true },
];

const FLUJO = [
  { n: 1, titulo: 'Configura en TESTNET', detalle: 'Estrategia, par, timeframe, riesgo y llaves del TESTNET de Binance. Nada de dinero real todavía.', ruta: '/trading/binance', tituloTab: 'Binance' },
  { n: 2, titulo: 'Enciende el bot', detalle: 'Marca "activo". El motor Python lee la configuración de la BD en cada ciclo — los cambios aplican sin reiniciar.', ruta: '/trading/binance', tituloTab: 'Binance' },
  { n: 3, titulo: 'Observa en el Dashboard', detalle: 'Balance, equity, P&L del día, posiciones abiertas y el heartbeat del motor.', ruta: '/trading/dashboard', tituloTab: 'Dashboard Trading' },
  { n: 4, titulo: 'Revisa las operaciones', detalle: 'Cada orden y trade queda registrado, con export a Excel.', ruta: '/trading/transacciones', tituloTab: 'Transacciones' },
  { n: 5, titulo: 'Valida semanas, no días', detalle: 'Solo después de resultados consistentes en testnet considera pasar a producción — con montos pequeños.', ruta: '/trading/dashboard', tituloTab: 'Dashboard Trading' },
];

const COMO: { q: string; pasos: string[]; enlaces: { to: string; t: string }[] }[] = [
  {
    q: '¿Cómo detengo el bot YA (kill switch)?',
    pasos: [
      'En Binance (configuración) activa el KILL SWITCH con su motivo — el motor se detiene en su siguiente ciclo (segundos).',
      'El kill switch es el freno de emergencia: manda sobre todo lo demás, incluida la señal de la estrategia.',
      'Para reanudar: apágalo y verifica que "activo" siga encendido.',
    ],
    enlaces: [{ to: '/trading/binance', t: 'Binance' }],
  },
  {
    q: '¿Cómo configuro las llaves de Binance de forma segura?',
    pasos: [
      'Crea las llaves en Binance con permisos de SOLO lectura + spot trading — JAMÁS habilites retiros (withdrawals).',
      'Pégalas en Binance → Llaves API: se guardan cifradas en reposo; la pantalla nunca vuelve a mostrarlas.',
      'Solo el motor del bot (rol de BD dedicado) puede descifrarlas — ni la app ni los usuarios.',
      'Empieza con las llaves del TESTNET (testnet.binance.vision) hasta validar la estrategia.',
    ],
    enlaces: [{ to: '/trading/binance', t: 'Binance' }],
  },
  {
    q: '¿Cómo controlo el riesgo?',
    pasos: [
      'En los parámetros de riesgo defines el tamaño máximo por posición, pérdida máxima diaria y límites de exposición.',
      'El motor evalúa el riesgo ANTES de cada orden: si un límite se supera, no opera (y puede encender el kill switch solo).',
      'Revisa el P&L del día en el Dashboard: si el número te incomoda, kill switch y a revisar la estrategia.',
    ],
    enlaces: [{ to: '/trading/binance', t: 'Binance' }, { to: '/trading/dashboard', t: 'Dashboard Trading' }],
  },
  {
    q: 'El Dashboard no se actualiza — ¿qué reviso?',
    pasos: [
      'Mira el heartbeat: si tiene más de 5 minutos, el motor Python está caído o sin conexión.',
      'El motor corre como servicio en el servidor (systemd trading-bot); TI puede revisarlo con systemctl status trading-bot.',
      'Verifica también el checklist de arriba: llaves configuradas, activo encendido y kill switch apagado.',
    ],
    enlaces: [{ to: '/trading/dashboard', t: 'Dashboard Trading' }],
  },
  {
    q: '¿Quién puede tocar la configuración del bot?',
    pasos: [
      'Cualquier usuario con la opción en su menú puede VER el dashboard y las transacciones.',
      'Cambiar configuración, llaves, activo o kill switch exige nivel de acceso 5 (administrador) — el servidor lo valida siempre.',
      'La cuenta de trading es GLOBAL: no pertenece a una empresa del ERP; opera una sola cuenta de Binance.',
    ],
    enlaces: [{ to: '/trading/binance', t: 'Binance' }],
  },
];

const GLOSARIO: { t: string; d: string }[] = [
  { t: 'Testnet', d: 'Entorno de práctica de Binance con dinero ficticio: mismas APIs, cero riesgo. SIEMPRE se valida ahí primero.' },
  { t: 'Kill switch', d: 'Freno de emergencia: detiene el bot de inmediato sin importar la estrategia. Se enciende a mano o por límites de riesgo.' },
  { t: 'Heartbeat', d: 'Señal de vida que el motor escribe en cada ciclo; si envejece, el bot está caído.' },
  { t: 'Estrategia', d: 'La lógica de decisión (señales de compra/venta) que el motor ejecuta sobre el par configurado.' },
  { t: 'P&L del día', d: 'Ganancia o pérdida realizada + no realizada del día en dólares.' },
  { t: 'Par / symbol', d: 'El mercado que opera el bot, por ejemplo BTC/USDT.' },
];

function Acordeon({ q, children }: { q: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border rounded-md">
      <button className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-left" onClick={() => setOpen((o) => !o)}>
        {q} {open ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {open && <div className="px-3 pb-3 text-sm text-muted-foreground">{children}</div>}
    </div>
  );
}

export function AyudaTrading() {
  const ir = useIr();
  const { data, isFetching } = useQuery<AyudaData>({
    queryKey: ['trading-ayuda'],
    queryFn: async () => (await apiClient.post('/rpc/trading_ayuda_estado', {}, TRD)).data,
    staleTime: 0, refetchOnMount: 'always',
  });

  const Ir = ({ to, t }: { to: string; t: string }) => (
    <button className="inline-flex items-center gap-1 text-primary hover:underline" onClick={() => ir(to, t)}>
      <Link2 className="w-3.5 h-3.5" /> {t}
    </button>
  );
  const e = data?.estado;

  return (
    <div className="p-6 space-y-4 max-w-5xl">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <LifeBuoy className="w-5 h-5" /> Ayuda — Trading
        {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
      </h1>

      {e?.kill_switch && (
        <div className="flex items-center gap-2 rounded-md border border-red-400 bg-red-50 px-4 py-2 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
          <AlertTriangle className="w-4 h-4" /> KILL SWITCH ACTIVO — el bot está detenido{e.kill_switch_motivo ? `: ${e.kill_switch_motivo}` : ''}.
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Listo para operar</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {CHECKS.map((c) => {
            const ok = data?.checklist?.[c.key] ?? false;
            return (
              <div key={c.key} className="flex items-start gap-2 text-sm">
                {ok
                  ? <CheckCircle2 className="w-4 h-4 mt-0.5 text-green-600 shrink-0" />
                  : <XCircle className={`w-4 h-4 mt-0.5 shrink-0 ${c.advisory ? 'text-amber-500' : 'text-red-500'}`} />}
                <div>
                  <span className={ok ? '' : 'font-medium'}>{c.label}</span>
                  <span className="text-muted-foreground"> — {c.ayuda}</span>{' '}
                  {!ok && <Ir to={c.ruta} t={c.titulo} />}
                </div>
              </div>
            );
          })}
          {e && (
            <div className="text-xs text-muted-foreground border-t pt-2 mt-2">
              Ambiente: <span className={`font-medium ${e.ambiente === 'testnet' ? 'text-emerald-600' : 'text-amber-600'}`}>{e.ambiente ?? '—'}</span>
              {e.symbol ? ` · Par: ${e.symbol}` : ''}{e.estrategia ? ` · Estrategia: ${e.estrategia}` : ''}
              {e.balance_usd != null ? ` · Balance: $${Number(e.balance_usd).toLocaleString(LOCALE)}` : ''}
              {e.pnl_dia_usd != null ? ` · P&L hoy: $${Number(e.pnl_dia_usd).toLocaleString(LOCALE)}` : ''}
              {e.heartbeat ? ` · Último heartbeat: ${new Date(e.heartbeat).toLocaleTimeString(LOCALE)}` : ''}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">El flujo del bot (testnet primero, SIEMPRE)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {FLUJO.map((f) => (
            <div key={f.n} className="flex items-start gap-3 text-sm">
              <div className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0">{f.n}</div>
              <div>
                <span className="font-medium">{f.titulo}.</span>{' '}
                <span className="text-muted-foreground">{f.detalle}</span>{' '}
                <Ir to={f.ruta} t={f.tituloTab} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">¿Cómo hago…?</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {COMO.map((c) => (
            <Acordeon key={c.q} q={c.q}>
              <ol className="list-decimal ml-5 space-y-1">
                {c.pasos.map((p, i) => <li key={i}>{p}</li>)}
              </ol>
              <div className="flex flex-wrap gap-3 mt-2">
                {c.enlaces.map((en) => <Ir key={en.to} to={en.to} t={en.t} />)}
              </div>
            </Acordeon>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Glosario</CardTitle></CardHeader>
        <CardContent className="grid gap-2 md:grid-cols-2">
          {GLOSARIO.map((g) => (
            <div key={g.t} className="text-sm">
              <span className="font-medium">{g.t}:</span>{' '}
              <span className="text-muted-foreground">{g.d}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
